/**
 * Load Data Pipeline → Overview.
 *
 * The one page that answers "is the pipeline alive, and what is in it". Every
 * figure is scoped to a window, because an unqualified total over a
 * partitioned table grows forever and stops meaning anything.
 */

import Box from '@mui/joy/Box'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import * as React from 'react'
import { loadPipelineApi } from 'api/loadPipeline'
import { ErrorState, Loading, Stat, StatRow, WindowPicker, useLoadPipelineData, formatBytes, formatLag, formatNumber } from './components/shared'

export default function LoadPipelineOverviewPage() {
    const [hours, setHours] = React.useState(24)
    const { data, error, loading } = useLoadPipelineData(() => loadPipelineApi.getOverview(hours), [hours])

    const lag = formatLag(data?.summary.lagSeconds ?? null)
    const peak = React.useMemo(() => Math.max(1, ...(data?.throughput ?? []).map((p) => p.observations)), [data])
    const emptyRollups = (data?.rollups ?? []).filter((r) => r.rows === 0)

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography level="h2">Load Data Pipeline</Typography>
                <WindowPicker hours={hours} onChange={setHours} />
            </Box>
            <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 3 }}>
                DAT observer → the load topic → Market Intel consumer → <code>loadpipe_intel_hourly</code>. Read-only.
            </Typography>

            {error ? <ErrorState error={error} /> : null}
            {loading && !data ? <Loading /> : null}

            {data ? (
                <>
                    <StatRow>
                        <Stat label="Observations" value={formatNumber(data.summary.observations)} hint="rates that survived normalization" />
                        <Stat label="Lanes" value={formatNumber(data.summary.lanes)} hint="distinct origin→destination" />
                        <Stat label="Cells" value={formatNumber(data.summary.rows)} hint="lane × equipment × hour × writer" />
                        <Stat label="Writers" value={formatNumber(data.summary.writers)} hint="concurrent consumer instances" />
                        <Stat label="Freshness" value={lag.text} hint="since newest hourly bucket" tone={lag.tone} />
                    </StatRow>

                    {emptyRollups.length > 0 ? (
                        <Card variant="soft" color="warning" sx={{ mb: 3 }}>
                            <Typography level="title-sm">Rollup tables are empty</Typography>
                            <Typography level="body-sm">
                                {emptyRollups.map((r) => r.table).join(' and ')} {emptyRollups.length === 1 ? 'has' : 'have'} no rows. The maintenance cycle
                                rolls hourly into daily and builds the snapshot from it — if this persists, the cycle is not running or not finishing. Lane
                                Prices and Heat Map read that snapshot, so they go stale with it.
                            </Typography>
                        </Card>
                    ) : null}

                    <Card sx={{ mb: 3 }}>
                        <Typography level="title-md" sx={{ mb: 2 }}>
                            Throughput by hour
                        </Typography>
                        {data.throughput.length === 0 ? (
                            <Typography level="body-sm" textColor="text.tertiary">
                                No observations in this window.
                            </Typography>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 140 }}>
                                {[...data.throughput].reverse().map((p) => (
                                    <Box
                                        key={p.bucketHour}
                                        title={`${new Date(p.bucketHour).toLocaleString()} — ${formatNumber(p.observations)} observations, ${formatNumber(p.lanes)} lanes`}
                                        sx={{
                                            flex: 1,
                                            minWidth: 4,
                                            height: `${Math.max(2, (p.observations / peak) * 100)}%`,
                                            bgcolor: 'primary.solidBg',
                                            borderRadius: 2,
                                        }}
                                    />
                                ))}
                            </Box>
                        )}
                    </Card>

                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <Sheet variant="outlined" sx={{ flex: 1, minWidth: 320, borderRadius: 'sm', p: 2 }}>
                            <Typography level="title-md" sx={{ mb: 1 }}>
                                Equipment
                            </Typography>
                            <Table size="sm" stickyHeader>
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th style={{ textAlign: 'right' }}>Observations</th>
                                        <th style={{ textAlign: 'right' }}>Lanes</th>
                                        <th style={{ textAlign: 'right' }}>Avg $/mi</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.equipment.map((e) => (
                                        <tr key={e.equipment}>
                                            <td>
                                                <Chip size="sm" variant="soft">
                                                    {e.equipment}
                                                </Chip>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>{formatNumber(e.observations)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatNumber(e.lanes)}</td>
                                            <td style={{ textAlign: 'right' }}>{e.avgRpm === null ? '—' : `$${e.avgRpm.toFixed(2)}`}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>

                        <Sheet variant="outlined" sx={{ flex: 1, minWidth: 320, borderRadius: 'sm', p: 2 }}>
                            <Typography level="title-md" sx={{ mb: 0.5 }}>
                                Storage
                            </Typography>
                            <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 1 }}>
                                Range-partitioned by hour so retention is a DROP, not a DELETE — dropping a partition leaves no dead tuples to vacuum.
                            </Typography>
                            <Table size="sm">
                                <thead>
                                    <tr>
                                        <th>Partition</th>
                                        <th style={{ textAlign: 'right' }}>Size</th>
                                        <th style={{ textAlign: 'right' }}>Rows (est.)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.partitions.map((p) => (
                                        <tr key={p.name}>
                                            <td>
                                                <code>{p.name}</code>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>{formatBytes(p.sizeBytes)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatNumber(p.rows)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    </Box>
                </>
            ) : null}
        </Box>
    )
}
